"use client";

import Image from "next/image";
import { useState } from "react";

import { MuxPlayer } from "@/components/post-rich-text/mux-player";
import { Container } from "@/components/ui/container";
import {
  Dialog,
  DialogClose,
  DialogOverlay,
  DialogPopup,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { Section } from "@/components/ui/section";
import { cn } from "@/lib/utils";

type FounderCard = {
  type: "founder";
  avatar: string;
  name: string;
  role: string;
  quote: string;
};

type QuoteCard = {
  type: "quote";
  quote: string;
  logo: string;
};

type ResultCard = {
  type: "result";
  bg: string;
  light?: boolean;
  logo: string;
  stat: string;
  caption: string;
};

type VideoCard = {
  type: "video";
  image: string;
  alt: string;
  playbackId: string;
};

export type TestimonialCard = FounderCard | QuoteCard | ResultCard | VideoCard;

interface TestimonialMarqueeProps {
  title: string;
  cards: TestimonialCard[];
  className?: string;
}

const LOGOS: Record<string, string> = {
  "True Classic": "/images/wayflyer-intelligence/cust-true-classic.png",
  SPONGELLÉ: "/images/wayflyer-intelligence/cust-spongelle.png",
  KEKOA: "/images/wayflyer-intelligence/cust-kekoa.png",
  "TRUE CLASSIC": "/images/wayflyer-intelligence/cust-true-classic.png",
};

function CompanyLogo({ name, light }: { name: string; light?: boolean }) {
  const src = LOGOS[name];
  if (!src) {
    return (
      <span
        className={cn(
          "text-[13px] tracking-[0.3em]",
          light ? "text-[#babfc3]" : "text-black"
        )}
      >
        {name}
      </span>
    );
  }
  return (
    <Image
      src={src}
      alt={name}
      width={120}
      height={28}
      className={cn(
        "block h-6 w-auto object-contain object-left",
        light && "brightness-0 invert"
      )}
    />
  );
}

function VideoCardView({ card }: { card: VideoCard }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Play video: ${card.alt}`}
        className="relative h-[280px] w-[400px] shrink-0 cursor-pointer overflow-hidden rounded-2xl border border-[#e5e6e6]"
      >
        <Image
          src={card.image}
          alt={card.alt}
          fill
          sizes="400px"
          className="object-cover"
        />
        <div className="absolute top-1/2 left-1/2 flex size-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-xl bg-white/20 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.2)] backdrop-blur-[6px]">
          <svg
            width="10"
            height="11"
            viewBox="0 0 10 11"
            fill="#ffffff"
            className="ml-0.5"
            aria-hidden="true"
          >
            <path d="M 9.375 4.433 C 10.208 4.907 10.208 6.093 9.375 6.567 L 1.876 10.833 C 1.042 11.307 0 10.715 0 9.766 L 0 1.234 C 0 0.285 1.042 -0.307 1.876 0.167 L 9.375 4.433 Z" />
          </svg>
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogPortal>
          <DialogOverlay className="bg-surface-inverse/80 backdrop-blur" />
          <DialogPopup
            initialFocus={false}
            className="fixed inset-0 z-50 flex items-center justify-center outline-none"
          >
            <DialogTitle className="sr-only">{card.alt}</DialogTitle>
            <div className="rounded-container relative w-full max-w-[90vw] p-4 md:p-6 lg:max-w-[80vw]">
              <div className="rounded-container relative aspect-video overflow-hidden">
                <MuxPlayer
                  playbackId={card.playbackId}
                  autoPlay="any"
                  loading="page"
                  preload="auto"
                  renditionOrder="desc"
                  minResolution="1080p"
                  className="absolute inset-0"
                />
              </div>
              <DialogClose className="bg-icon-primary [&>svg]:stroke-icon-on-dark top-10 right-10 rounded-none p-2 md:top-12 md:right-12" />
            </div>
          </DialogPopup>
        </DialogPortal>
      </Dialog>
    </>
  );
}

function TestimonialCardView({ card }: { card: TestimonialCard }) {
  if (card.type === "founder") {
    return (
      <div className="flex h-[280px] w-[400px] shrink-0 flex-col gap-6 overflow-hidden rounded-2xl border border-[#e5e6e6] bg-white p-8">
        <div className="flex flex-row items-center gap-4">
          <div className="relative size-16 shrink-0 overflow-hidden rounded-lg bg-gray-100">
            <Image
              src={card.avatar}
              alt=""
              fill
              sizes="64px"
              className="object-cover"
            />
          </div>
          <div className="flex flex-col">
            <span className="text-[16px] leading-[1.4] tracking-[-0.02em] text-black">
              {card.name}
            </span>
            <span className="text-[14px] leading-[1.4] tracking-[-0.02em] text-[#7b8084]">
              {card.role}
            </span>
          </div>
        </div>
        <div className="h-px shrink-0 bg-[#cacccc]" />
        <span className="text-[18px] leading-[1.3] tracking-[-0.02em] text-pretty text-black">
          {card.quote}
        </span>
      </div>
    );
  }

  if (card.type === "quote") {
    return (
      <div className="flex h-[280px] w-[400px] shrink-0 flex-col justify-between gap-6 overflow-hidden rounded-2xl border border-[#e5e6e6] bg-white p-8">
        <span className="text-[17px] leading-[1.3] tracking-[-0.02em] text-pretty text-black">
          {card.quote}
        </span>
        <CompanyLogo name={card.logo} />
      </div>
    );
  }

  if (card.type === "result") {
    return (
      <div
        className="flex h-[280px] w-[400px] shrink-0 flex-col items-start justify-between overflow-hidden rounded-2xl border border-[#e5e6e6] p-8"
        style={{ background: card.bg }}
      >
        <CompanyLogo name={card.logo} light={card.light} />
        <div className="flex flex-col gap-1.5">
          <span
            className={cn(
              "text-[64px] leading-[1.1] font-normal tracking-[-0.04em]",
              card.light ? "text-white" : "text-black"
            )}
          >
            {card.stat}
          </span>
          <span
            className={cn(
              "text-[14px] leading-[1.4] tracking-[-0.02em]",
              card.light ? "text-white" : "text-black"
            )}
          >
            {card.caption}
          </span>
        </div>
      </div>
    );
  }

  return <VideoCardView card={card} />;
}

export function TestimonialMarquee({
  title,
  cards,
  className,
}: TestimonialMarqueeProps) {
  if (cards.length === 0) return null;

  // Split into two distinct sets (by index parity) so the two rows show
  // different cards, each duplicated for the seamless scroll loop.
  const rowACards = cards.filter((_, i) => i % 2 === 0);
  const rowBCards = cards.filter((_, i) => i % 2 === 1);
  const rowA = [...rowACards, ...rowACards];
  const rowB = [...rowBCards, ...rowBCards];

  return (
    <Section className={cn(className, "overflow-hidden bg-[#fafaf7] lg:px-0")}>
      <Container>
        <h2 className="col-span-full mb-12 text-center text-[32px] leading-[1.2] font-normal tracking-[-0.04em] text-balance text-black lg:text-[40px]">
          {title}
        </h2>
        <div className="group col-span-full flex flex-col gap-5">
          <div className="overflow-hidden">
            {/* pr-5 matches the gap-5 between cards: the track's own width
                (w-max) has no gap trailing its last card, so translateX(-50%)
                would land 10px (half a gap) short of the true repeat unit —
                a visible jump at every loop. The extra padding makes 50% of
                the track's width exactly one repeat unit. */}
            <div className="animate-wf-marquee-left flex w-max flex-row gap-5 pr-5 group-hover:[animation-play-state:paused] motion-reduce:w-auto motion-reduce:[animation:none] motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:pr-0">
              {rowA.map((card, i) => {
                const isDuplicate = i >= rowACards.length;
                return (
                  <div
                    key={`a-${i}`}
                    aria-hidden={isDuplicate || undefined}
                    inert={isDuplicate}
                    className={isDuplicate ? "motion-reduce:hidden" : undefined}
                  >
                    <TestimonialCardView card={card} />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="overflow-hidden">
            <div className="animate-wf-marquee-right flex w-max flex-row gap-5 pr-5 group-hover:[animation-play-state:paused] motion-reduce:w-auto motion-reduce:[animation:none] motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:pr-0">
              {rowB.map((card, i) => {
                const isDuplicate = i >= rowBCards.length;
                return (
                  <div
                    key={`b-${i}`}
                    aria-hidden={isDuplicate || undefined}
                    inert={isDuplicate}
                    className={isDuplicate ? "motion-reduce:hidden" : undefined}
                  >
                    <TestimonialCardView card={card} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}
